import Layout from 'components/Layout'
import { StaticImage } from 'gatsby-plugin-image'
import { transformSidebarsToStructuredObject } from '../../../gatsby/utils'
import React, { useState, useRef, useEffect } from 'react'
import HomeCTA from 'components/HomeCTA'
import 'components/Pricing/styles/index.scss'
import { graphql, useStaticQuery } from 'gatsby'
import { useI18next } from 'gatsby-plugin-react-i18next'
import { shortcodes } from '../../mdxGlobalComponents'
import { BillingProductV2Type, IOtherOptionsSection } from 'types'
import { FAQs } from 'components/FAQs'
import { PricingApproach } from 'components/Pricing/PricingApproach'
import { GitHub } from 'components/Icons'
import { Enterprise as EnterpriseIcon } from 'components/NotProductIcons'
import { Quote } from 'components/Pricing/Quote'
import { MenuItem, InternalMenu } from 'components/MainNav/utils'
import { ShowPlan } from 'components/Pricing/ShowPlan'
import Features from 'components/Pricing/Features'
import { PricingFooterCTA } from 'components/Pricing/PricingFooterCTA'
import { recursiveSearch } from 'hooks/useLayoutData'
import AllPlans from 'components/Pricing/AllPlans'
import { useLocation } from '@reach/router'
import { SEO } from 'components/seo'
import { SEO2 } from 'components/seo2'
import { MDXProvider } from '@mdx-js/react'
import { MDXRenderer } from 'gatsby-plugin-mdx'
import cntl from 'cntl'
import { PricingHero } from 'components/Pricing/PricingHero'
import { PlanComparison } from './PlanComparison'
import OtherOptions from './OtherOptions'
import { PricingCalculator } from './PricingCalculator'
import SimplePlans from './SimplePlans'
import { getSeoImage } from 'utils/googleData'
// url to group names mapping
const internalProductNames = {
    'property-management': 'property_management',
    'real-estate-crm': 'realestate_crm',
    'real-estate-marketing': 'realestate_marketing',
    'real-estate-websites': 'websites',
}

export const section = cntl`
    max-w-6xl
    xl:max-w-7xl
    mx-auto
    px-4
`

export const gridCell = cntl`
    bg-white
    flex
    flex-col
    px-4
    xl:px-8
    shadow-xl
`

export const gridCellTop = cntl`
    bg-white/40
    rounded-t-md
    pt-4
    xl:pt-8
`

export const gridCellMid = cntl`
    pt-4
    xl:pt-6
    pb-8
`

export const gridCellBottom = cntl`
    mb-12
    lg:mb-0
    pb-4
    xl:pb-8
    rounded-b-md
`

// export const Head = ({ location, params, data: { pageData }, pageContext: { documentationNav } }) => (
//     <SEO2
//         title={'iCloudReady Pricing'}
//         description={`Find out how much it costs to use iCloudReady`}
//         image={`/images/product/${pageData?.fields?.slug?.split('/')[1]}.png`}
//     />
// )

export const SectionWrapper = ({ children, className = '' }: ISectionWrapper) => {
    return (
        <section className={`${className} mb-12 mt-12 md:px-4`}>
            <div>{children}</div>
        </section>
    )
}

export default function Pricing(pageProps) {
    const { data, location, pageContext, params, children } = pageProps
    const { pageData, layoutData, blogPosts, documentation, tutorials, customers } = data
    const staticProducts: BillingProductV2Type[] = pageData?.nodes[0]?.parent?.products
    const layout = layoutData?.nodes[0]?.parent?.layout
    const sidebars = transformSidebarsToStructuredObject(layout?.sidebars)
    const sidebars_ar = transformSidebarsToStructuredObject(layout?.sidebars_ar)
    const {
        body,
        excerpt,
        frontmatter,
        parent,
        fields: { slug },
    } = pageData?.nodes[0]
    const {
        exclude,
        title,
        subtitle,
        description,
        pricingMainCTA,
        showCTA,
        seoTitle,
        seoDescription,
        seoimage,
        stickeyMenu,
    } = frontmatter
    const [currentModal, setCurrentModal] = useState<string | boolean>(false)
    const { search, pathname } = useLocation()
    const { originalPath } = useI18next()
    const [groupsToShow, setGropsToShow] = useState<undefined | string[]>()
    // if (exclude && !process.env.RENDER_DISABLED_COMPONENTS) {
    //     return <></>
    // }
    useEffect(() => {
        const product = new URLSearchParams(search).get('product')
        setGropsToShow((product && [internalProductNames[product]]) || undefined)
    }, [search])

    const currentGroup = groupsToShow?.[0]
    const components = {
        ...shortcodes(pageProps),
        Hero: (props: any) => <PricingHero {...props} googleImages={[parent?.images, parent?.images2]} />,
        SimplePlans: (props: any) => <SimplePlans {...props} googleImages={[parent?.images, parent?.images2]} />,
        // ShowPlan: (props: any) => (
        //     <div id="pricingShowPlan">
        //         <ShowPlan
        //  {...props}
        //         />
        //      </div>
        // ),
        // Features: (props: any) => (
        //     <div id="pricingFeatures">
        //         <SectionWrapper {...props} className={`${section} mt-12 md:px-4`}>
        //         <h2 className="text-2xl m-0 flex items-center">What comes in iCloudReady?</h2>
        //         <p className="m-0  font-medium mb-7">Get access to all features and no plan limits.</p>
        //         <Features
        //  {...props}
        //         />
        //                     </SectionWrapper>
        //      </div>
        // ),
        // AllPlans: (props: any) => (
        //     <div id="pricingAllPlans">
        //     <SectionWrapper {...props} className={`${section} mb-12 mt-12 md:mt-24 md:px-4`}>
        //     <h2 className="text-2xl m-0 flex items-center border-b border-dashed border-gray-accent-light pb-4">
        //             Compare all plans
        //         </h2>
        //         <AllPlans
        //  {...props}
        //         />
        //  </SectionWrapper>
        //      </div>
        // ),
        PlanComparison: (props: any) => (
            <div id="pricingPlanComparison">
                <SectionWrapper {...props} className={section}>
                    <PlanComparison
                        {...props}
                        showCTA={showCTA}
                        groupsToShow={groupsToShow}
                        products={staticProducts}
                        googleImages={[parent?.images, parent?.images2]}
                    />
                </SectionWrapper>
            </div>
        ),
        PricingCalculator: (props: any) => (
            <PricingCalculator {...props} googleImages={[parent?.images, parent?.images2]} />
        ),
        OtherOptions: (props: any) => (
            <div id="pricingOtherOptions">
                <SectionWrapper {...props} className={`${section} mb-12 mt-12 md:mt-24 md:px-4`}>
                    <OtherOptions {...props} googleImages={[parent?.images, parent?.images2]} />
                </SectionWrapper>
            </div>
        ),
        FAQs: (props: any) => (
            <div id="pricingFAQs">
                <SectionWrapper {...props} className={`${section} mb-12 mt-12 md:mt-24 md:px-4`}>
                    <FAQs {...props} googleImages={[parent?.images, parent?.images2]} />
                </SectionWrapper>
            </div>
        ),
        PricingQuote: (props: any) => <Quote {...props} googleImages={parent?.images} />,
        PricingFooterCTA: (props: any) => (
            <div id="pricingFooterCTA">
                <SectionWrapper {...props} className={`${section} mb-12 mt-16`}>
                    <PricingFooterCTA {...props} googleImages={[parent?.images, parent?.images2]} />
                </SectionWrapper>
            </div>
        ),
        HomeCTA: (props: any) => <HomeCTA {...props} googleImages={[parent?.images, parent?.images2]} />,
        Approach: (props: any) => <PricingApproach {...props} googleImages={[parent?.images, parent?.images2]} />,
    }
    const menu = sidebars.menu
    const pricingMenu = sidebars.pricing?.[0]
    let menuParent
    if (Array.isArray(menu)) {
        menuParent = menu.find(({ children, url }) => {
            const currentURL = originalPath || pathname
            return currentURL === url?.split('?')[0] || (children && recursiveSearch(children, currentURL))
        })
    } else {
        // console.warn('menu is not an array or is undefined')
        menuParent = undefined
    }

    const internalMenu = menuParent?.children
    let activeInternalMenu
    if (Array.isArray(pricingMenu?.children)) {
        activeInternalMenu =
            pricingMenu.children[Object.values(internalProductNames).findIndex((name) => name === currentGroup) + 1]
    } else {
        console.warn('pricingMenu children is not an array or is undefined')
        activeInternalMenu = undefined
    }

    return (
        <Layout>
            <SEO
                title={seoTitle}
                description={seoDescription}
                image={getSeoImage({ googleImages: [parent?.images, parent?.images2] }) || `/images/home.png`}
            />
            {stickeyMenu && (
                <InternalMenu
                    mobile
                    className="sticky top-0 z-10 -mx-4 mb-[-1px] border-t bg-red dark:bg-dark md:mx-0  lg:flex"
                    menu={internalMenu}
                    activeIndex={internalMenu?.findIndex((menu) => menu === activeInternalMenu)}
                />
            )}
            <MDXProvider components={components}>
                <MDXRenderer>{body}</MDXRenderer>
            </MDXProvider>
        </Layout>
    )
}
